const TABBABLE = 'a[href], area[href], button, input, select, textarea, iframe, summary, [contenteditable=""], [contenteditable="true"], [tabindex]';

/** The focused element, followed down through open shadow roots. */
export function deepActiveElement(root: Document | ShadowRoot = document): HTMLElement | null {
  let el = root.activeElement as HTMLElement | null;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement as HTMLElement;
  return el;
}

function isTabbable(el: HTMLElement): boolean {
  if (!el.matches(TABBABLE)) return false;
  if ((el as HTMLButtonElement).disabled) return false;
  if (el.tabIndex < 0) return false;
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  if (el.closest('[inert]')) return false;
  if (el.getClientRects().length === 0) return false;
  return getComputedStyle(el).visibility !== 'hidden';
}

/**
 * Tabbable elements under `root` in rendered order, including those inside
 * open shadow roots and content projected into slots.
 */
export function tabbableElements(root: Element): HTMLElement[] {
  const out: HTMLElement[] = [];
  const visit = (node: Element): void => {
    if (node instanceof HTMLElement && isTabbable(node)) out.push(node);
    if (node instanceof HTMLSlotElement) {
      node.assignedElements({ flatten: true }).forEach(visit);
      return;
    }
    const children = node.shadowRoot ? node.shadowRoot.children : node.children;
    for (const child of Array.from(children)) visit(child);
  };
  for (const child of Array.from(root.shadowRoot ? root.shadowRoot.children : root.children)) visit(child);
  return out;
}

/** Whether `el` sits inside `container`, crossing shadow boundaries. */
export function containsDeep(container: Element, el: Node | null): boolean {
  let node: Node | null = el;
  while (node) {
    if (node === container) return true;
    node = node.parentNode ?? (node instanceof ShadowRoot ? node.host : null);
  }
  return false;
}

/**
 * Keeps Tab and Shift+Tab inside `container`. Call from a keydown handler;
 * returns true when it moved focus.
 */
export function trapTabKey(event: KeyboardEvent, container: Element): boolean {
  if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return false;
  const items = tabbableElements(container);
  if (items.length === 0) {
    event.preventDefault();
    return true;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = deepActiveElement();
  const inside = containsDeep(container, active);
  if (event.shiftKey && (!inside || active === first)) {
    event.preventDefault();
    last.focus();
    return true;
  }
  if (!event.shiftKey && (!inside || active === last)) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/**
 * Moves focus into `container`: to the first element marked `data-autofocus`
 * (its inner control when it is a shadow host), else to `fallback`. When the
 * marked element can't take focus (disabled or hidden), focus goes to the
 * first tabbable element inside `preferred`, and only then to `fallback`, so
 * it never stays behind the dialog.
 */
export function focusInitial(container: Element, fallback: HTMLElement, preferred?: Element | null): void {
  const marked = findMarked(container);
  if (!marked) {
    fallback.focus();
    return;
  }
  const target = marked.shadowRoot ? tabbableElements(marked)[0] ?? marked : marked;
  if (takesFocus(target)) return;
  const first = preferred ? tabbableElements(preferred)[0] : undefined;
  if (first && takesFocus(first)) return;
  fallback.focus();
}

/** Focuses `el` and reports whether focus actually arrived there. */
function takesFocus(el: HTMLElement): boolean {
  el.focus();
  return deepActiveElement() === el;
}

function findMarked(root: Element): HTMLElement | null {
  const direct = root.querySelector<HTMLElement>('[data-autofocus]');
  if (direct) return direct;
  for (const el of Array.from(root.querySelectorAll('*'))) {
    if (el.shadowRoot) {
      const inner = el.shadowRoot.querySelector<HTMLElement>('[data-autofocus]');
      if (inner) return inner;
    }
  }
  return null;
}

/** Returns focus to `el` if it is still in the document. */
export function restoreFocus(el: HTMLElement | null): void {
  if (el && el.isConnected) el.focus();
}
